import { Component, OnDestroy } from '@angular/core';
import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, finalize, map, of, startWith, Subscription, switchMap, tap } from 'rxjs';
import { Pagination } from 'src/app/accountancy/utilities/classes/pagination.class';
import { Routes } from 'src/app/accountancy/utilities/classes/routes.class';
import { WaitHandler } from 'src/app/accountancy/utilities/classes/wait-handler.class';
import { ReimbursementService } from './reimbursement-service/reimbursement.service';
import { ManageApproversService } from '../../account-management/manage-approvers/manage-approvers-service/manage-approvers.service';
import { LoginService } from 'src/app/accountancy/authentication/login/login-service/login.service';
import { GlobalApiService } from 'src/app/accountancy/utilities/services/global-api/global-api.service';
import { AccSimpleInputTextFormService } from 'src/app/accountancy/custom-components/acc-simple-input-text-form/acc-simple-input-text-form-service/acc-simple-input-text-form.service';
import { MatDialog } from '@angular/material/dialog';
import { FormGroup } from '@angular/forms';
import { Helper } from 'src/app/accountancy/utilities/classes/helper.class';
import { AccTableComponent } from 'src/app/accountancy/custom-components/acc-table/acc-table.component';
import { AccSearchTipComponent } from 'src/app/accountancy/custom-components/acc-search-tip/acc-search-tip.component';
import { ReimbursementFormComponent } from './reimbursement-form/reimbursement-form.component';
import { ReimbursementInterface } from 'src/app/accountancy/utilities/interfaces/reimbursement.interface';

@Component({
  selector: 'app-reimbursement',
  templateUrl: './reimbursement.component.html',
  styleUrls: ['./reimbursement.component.scss']
})
export class ReimbursementComponent implements OnDestroy {

  public readonly routes = Routes;
  public readonly Helper = Helper;

  public readonly waitHandler = new WaitHandler();
  public readonly pagination = new Pagination();
  public readonly reimList$ = new BehaviorSubject<ReimbursementInterface[]>([]);

  public readonly fgReimFilter = this._newFgReimFilter();

  private readonly _subsGetReimList = this._getSubsGetReimList();

  public readonly reimApprovalStatuses$ = this._getReimApprovalStatuses();

  constructor(
    private _reimS: ReimbursementService,
    private _mApprovalS: ManageApproversService,
    private _loginS: LoginService,
    private _gApiS: GlobalApiService,
    private _simpleFormS: AccSimpleInputTextFormService,
    private _matDialog: MatDialog,
  ) { }

  public ngOnDestroy(): void {
    this._subsGetReimList.unsubscribe();
  }

  public readonly dialogs = {
    openSearch: () => this._matDialog.open(AccSearchTipComponent),
    openPrint: async (table: AccTableComponent) => table.printTable(),
  }

  private _getReimApprovalStatuses() {
    return of(null).pipe(
      tap(() => this.waitHandler.newRequest()),
      switchMap(() => this._mApprovalS.getApprovalLevels("Reimbursement").pipe(
        finalize(() => this.waitHandler.finishRequest()),
      ))
    )
  }

  private _newFgReimFilter() {
    return new FormGroup({
      Type: Helper.reactiveForms.control<string>('My Requests'),
      Status: Helper.reactiveForms.control<string>('All Status'),
    });
  }

  private _getSubsGetReimList() {
    return combineLatest([
      this.fgReimFilter.valueChanges.pipe(startWith(this.fgReimFilter.value)),
      this.pagination.searchQuery$.pipe(startWith(this.pagination.query), debounceTime(500), distinctUntilChanged()),
      this.pagination.pageLimit$,
      this.pagination.currentPage$,
      this.pagination.reloader$.pipe(startWith(null)),
    ]).pipe(
      tap(() => this.waitHandler.newRequest()),
      switchMap(([filter, query, limit]) => {

        const userId = this._loginS.userData?.ID;
        const requestFilter = {
          RequestedBy: filter?.Type && filter.Type === 'My Requests' ? userId : null,
          Approver: filter?.Type && filter.Type === 'For My Approval' ? userId : null,
          Status: filter?.Status && filter.Status === 'All Status' ? null : filter?.Status,
        };

        return this._reimS.getReimbursementRequests(this.pagination.start, limit, query, requestFilter).pipe(
          finalize(() => this.waitHandler.finishRequest()),
          map(values => values.body),
          tap(values => {
            this.pagination.setTotalCount(values?.total_rows);

            // const list = values?.data.map(v => ({ ...v, checked: false } as ReimbursementInterface)) ?? [];
            // const computedAmount = list.map(reim => ({ ...reim, ReimbursementAmount: reim.Items.reduce((c, n) => c + (n.VatableSale ?? 0), 0) }))
            // this.reimList$.next(computedAmount);
            this.reimList$.next(values?.data ?? []);
          }),
        );
      })
    ).subscribe();
  }

  public newReim() {
    const ref = this._matDialog.open(ReimbursementFormComponent, { disableClose: true });
    const subs: Subscription = ref.afterClosed().pipe(
      finalize(() => subs.unsubscribe())
    ).subscribe((() => this.pagination.reloader$.next()));
  }

}
